import type { FC, ReactNode } from 'react'
import type { Bi } from '@/sanskrit/lib/sanskrit/types'
import type { Analysis } from '@/sanskrit/lib/sanskrit/analyzer'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { motion } from 'motion/react'
import { BadgeCheck, CircleAlert, CircleHelp, Eraser } from 'lucide-react'
import { cn } from '@/sanskrit/lib/utils'
import { Seo } from '@/sanskrit/components/generals/seo'
import { Layout } from '@/sanskrit/components/generals/layout'
import { SanskritGate } from '@/components/plans/sanskrit-gate'
import { Button } from '@/components/ui/button'
import { Chip, PageHeader } from '@/sanskrit/components/vani/bits'
import { useLang, useT } from '@/sanskrit/components/providers/app-state'
import { analyzeSentence, checkAgreement } from '@/sanskrit/lib/sanskrit/analyzer'
import { getDhatu } from '@/sanskrit/lib/sanskrit/dhatus'
import { KRIDANTA_PRATYAYAS } from '@/sanskrit/lib/sanskrit/derivation'
import { verbGlossEn, whenOf, whoOf } from '@/sanskrit/lib/sanskrit/describe'
import { hasDevanagari, normalizeSanskrit } from '@/sanskrit/lib/sanskrit/script'
import { GENDER_LABEL, LAKARA_LABEL, PADA_LABEL, PURUSHA_LABEL, VACANA_LABEL, VIBHAKTI_LABEL } from '@/sanskrit/lib/sanskrit/labels'
import { KARAKA_ROLES } from '@/sanskrit/lib/content/karaka'
import AudioButton from '@/sanskrit/components/vani/audio-button'
import Skt from '@/sanskrit/components/vani/skt'
import TutorPanel from '@/sanskrit/components/vani/tutor-panel'

const EXAMPLES: Array<{ sa: string; label: Bi }> = [
    { sa: 'अहं संस्कृतं पठामि।', label: { hi: 'सरल वाक्य', en: 'A simple sentence' } },
    { sa: 'रामः सीता च वनं गच्छतः।', label: { hi: 'दो कर्ता', en: 'Two subjects' } },
    { sa: 'बालिकाः उद्याने क्रीडन्ति।', label: { hi: 'स्थान सहित', en: 'With a place' } },
    { sa: 'सः पठितुं विद्यालयं गच्छति।', label: { hi: 'कृदन्त सहित', en: 'With a participle' } },
    { sa: 'माता पुत्राय मोदकं ददाति।', label: { hi: 'देना', en: 'Giving' } },
    { sa: 'अहं पठति।', label: { hi: 'एक भूल पकड़िए', en: 'Spot a mistake' } }
]

const KIND_LABEL: Record<Analysis['kind'], Bi & { sa: string }> = {
    verb: { sa: 'क्रिया', hi: 'क्रिया', en: 'Verb' },
    noun: { sa: 'संज्ञा', hi: 'संज्ञा', en: 'Noun' },
    pronoun: { sa: 'सर्वनाम', hi: 'सर्वनाम', en: 'Pronoun' },
    adjective: { sa: 'विशेषणम्', hi: 'विशेषण', en: 'Adjective' },
    numeral: { sa: 'सङ्ख्या', hi: 'संख्या', en: 'Number' },
    kridanta: { sa: 'कृदन्तम्', hi: 'कृदन्त', en: 'Participle or infinitive' },
    indeclinable: { sa: 'अव्ययम्', hi: 'अव्यय', en: 'Indeclinable' },
    phrase: { sa: 'वाक्यांशः', hi: 'वाक्यांश', en: 'Set phrase' },
    derived: { sa: 'व्युत्पन्नम्', hi: 'व्युत्पन्न शब्द', en: 'Derived word' }
}

const ROLE_BY_VIBHAKTI = (Object.entries(KARAKA_ROLES) as Array<[keyof typeof KARAKA_ROLES, (typeof KARAKA_ROLES)[keyof typeof KARAKA_ROLES]]>)
    .filter(([id, info]) => info.vibhakti && id !== 'visesana')

/** One-line description of a reading, used for the alternatives list. */
const shortLabel = (a: Analysis, t: (b: Bi) => string): string => {
    switch (a.kind) {
        case 'verb':
            return `${getDhatu(a.dhatuId)?.root ?? ''} · ${LAKARA_LABEL[a.lakara].sa} · ${PURUSHA_LABEL[a.purusha].short} · ${VACANA_LABEL[a.vacana].short}`
        case 'noun':
        case 'pronoun':
        case 'adjective':
            return `${a.stem} · ${GENDER_LABEL[a.gender].short} · ${VIBHAKTI_LABEL[a.vibhakti].sa} · ${VACANA_LABEL[a.vacana].short}`
        case 'kridanta':
            return `${getDhatu(a.dhatuId)?.root ?? ''} + ${KRIDANTA_PRATYAYAS.find(p => p.id === a.pratyaya)?.name ?? ''}`
        default:
            return t(a.meaning)
    }
}

function ReadingDetails({ a }: { a: Analysis }) {
    const t = useT()

    if (a.kind === 'verb') {
        const d = getDhatu(a.dhatuId)

        return (
            <dl className='grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm'>
                <dt className='text-muted-foreground'>{t({ hi: 'धातु', en: 'Root' })}</dt>
                <dd><Link href={`/sanskrit/grammar/dhatu/${a.dhatuId}`} lang='sa' className='deva font-semibold text-saffron-strong underline-offset-4 hover:underline'>{d?.root}</Link> <span className='text-muted-foreground'>({t(d?.meaning)})</span></dd>
                <dt className='text-muted-foreground'>{t({ hi: 'लकार', en: 'Lakāra' })}</dt>
                <dd><span lang='sa' className='deva font-semibold'>{LAKARA_LABEL[a.lakara].sa}</span> <span className='text-muted-foreground'>({t(whenOf(a.lakara))})</span></dd>
                <dt className='text-muted-foreground'>{t({ hi: 'पुरुष, वचन', en: 'Person, number' })}</dt>
                <dd>{t({ hi: PURUSHA_LABEL[a.purusha].hi, en: PURUSHA_LABEL[a.purusha].en })}, {t({ hi: VACANA_LABEL[a.vacana].hi, en: VACANA_LABEL[a.vacana].en })}</dd>
                <dt className='text-muted-foreground'>{t({ hi: 'पद', en: 'Pada' })}</dt>
                <dd lang='sa' className='deva'>{PADA_LABEL[a.pada].sa}</dd>
                <dt className='text-muted-foreground'>{t({ hi: 'कौन करता है', en: 'Who acts' })}</dt>
                <dd>{t(whoOf(a.purusha, a.vacana))}</dd>
            </dl>
        )
    }

    if (a.kind === 'noun' || a.kind === 'pronoun' || a.kind === 'adjective') {
        const role = a.kind === 'adjective' ? KARAKA_ROLES.visesana : ROLE_BY_VIBHAKTI.find(([, info]) => info.vibhakti === a.vibhakti)?.[1]

        return (
            <dl className='grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm'>
                <dt className='text-muted-foreground'>{t({ hi: 'मूल शब्द', en: 'Stem' })}</dt>
                <dd><span lang='sa' className='deva font-semibold'>{a.stem}</span> <span className='text-muted-foreground'>({t(a.meaning)})</span></dd>
                <dt className='text-muted-foreground'>{t({ hi: 'लिङ्ग', en: 'Gender' })}</dt>
                <dd>{t({ hi: GENDER_LABEL[a.gender].hi, en: GENDER_LABEL[a.gender].en })}</dd>
                <dt className='text-muted-foreground'>{t({ hi: 'विभक्ति, वचन', en: 'Case, number' })}</dt>
                <dd><span lang='sa' className='deva font-semibold'>{VIBHAKTI_LABEL[a.vibhakti].sa}</span>, {t({ hi: VACANA_LABEL[a.vacana].hi, en: VACANA_LABEL[a.vacana].en })}</dd>
                {role && (
                    <>
                        <dt className='text-muted-foreground'>{t({ hi: 'सम्भावित भूमिका', en: 'Likely role' })}</dt>
                        <dd><span lang='sa' className='deva font-semibold'>{role.sa}</span> <span className='text-muted-foreground'>({t(role.question)})</span></dd>
                    </>
                )}
            </dl>
        )
    }

    if (a.kind === 'kridanta') {
        const p = KRIDANTA_PRATYAYAS.find(x => x.id === a.pratyaya)
        const d = getDhatu(a.dhatuId)

        return (
            <dl className='grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm'>
                <dt className='text-muted-foreground'>{t({ hi: 'बना है', en: 'Built from' })}</dt>
                <dd><span lang='sa' className='deva font-semibold'>{d?.root} + {p?.name}</span></dd>
                <dt className='text-muted-foreground'>{t({ hi: 'अर्थ', en: 'Meaning' })}</dt>
                <dd>{t(a.meaning)}</dd>
                <dt className='text-muted-foreground'>{t({ hi: 'प्रकार', en: 'Type' })}</dt>
                <dd>{t(p?.kind)}</dd>
            </dl>
        )
    }

    return <p className='text-sm'>{t(a.meaning)}</p>
}

const VakyaPage: FC = (): ReactNode => {
    const t = useT()
    const lang = useLang()
    const router = useRouter()
    const [text, setText] = useState(EXAMPLES[0].sa)
    const [chosen, setChosen] = useState<Record<string, number>>({})

    useEffect(() => {
        if (!router.isReady) return
        const s = router.query.s
        if (typeof s === 'string' && s.trim()) setText(s)
    }, [router.isReady, router.query.s])

    const sentence = normalizeSanskrit(text.trim())
    const tokens = useMemo(() => (sentence && hasDevanagari(sentence) ? analyzeSentence(sentence) : []), [sentence])
    const words = useMemo(() => tokens.filter(tok => tok.isWord), [tokens])
    const known = words.filter(w => w.analyses.length > 0)
    const issues = useMemo(() => (words.length > 0 ? checkAgreement(words.map(w => w.text)) : []), [words])
    const hasVerb = words.some(w => w.analyses.some(a => a.kind === 'verb'))

    const readingOf = (index: number) => {
        const w = words[index]
        const pick = chosen[`${sentence}:${index}`] ?? 0
        return w.analyses[Math.min(pick, w.analyses.length - 1)]
    }

    const glossOf = (a: Analysis): string => {
        if (a.kind === 'verb') {
            if (lang === 'en') return verbGlossEn(a)
            const d = getDhatu(a.dhatuId)
            return `${d?.meaning.hi ?? ''} (${whoOf(a.purusha, a.vacana).hi}, ${whenOf(a.lakara).hi})`
        }
        return t(a.meaning)
    }

    return (
        <Layout>
            <Seo title='Sentence analyser' description='Type any Sanskrit sentence and see every word explained: root, tense, person, case and number, plus a check that the subject and verb agree.' />

            <SanskritGate what='The sentence analyser'>
            <PageHeader
                sa='वाक्यविश्लेषणम्'
                title={{ hi: 'वाक्य-विश्लेषक', en: 'Sentence analyser' }}
                subtitle={{
                    hi: 'कोई भी संस्कृत वाक्य लिखिए। हर शब्द का व्याकरण, उसकी सम्भावित भूमिका और कर्ता-क्रिया का मेल यहाँ जाँचा जाता है।',
                    en: 'Type any Sanskrit sentence. Every word gets its grammar and likely role, and the subject and verb are checked for agreement.'
                }}
            />

            <div className='mx-auto max-w-6xl space-y-6 px-4 pb-16 sm:px-6'>
                <section className='rounded-[2rem] border border-border bg-card p-5 shadow-sm sm:p-6'>
                    <label htmlFor='vakya-input' className='text-sm font-semibold'>{t({ hi: 'वाक्य (देवनागरी में)', en: 'Sentence (in Devanagari)' })}</label>
                    <textarea
                        id='vakya-input'
                        lang='sa'
                        value={text}
                        onChange={e => setText(e.target.value)}
                        rows={2}
                        className='deva mt-2 w-full resize-y rounded-2xl border-2 border-input bg-background px-4 py-3 text-2xl leading-relaxed focus:border-saffron focus:outline-none'
                    />
                    <div className='mt-3 flex flex-wrap items-center gap-2'>
                        {EXAMPLES.map(ex => (
                            <button key={ex.sa} type='button' onClick={() => setText(ex.sa)} className={cn('rounded-full border px-3 py-1.5 text-sm transition-colors', ex.sa === sentence ? 'border-ink bg-ink text-primary-foreground' : 'border-border bg-background hover:border-saffron')}>
                                <span lang='sa' className='deva'>{ex.sa}</span>
                                <span className={cn('ml-2 text-xs', ex.sa === sentence ? 'text-primary-foreground/70' : 'text-muted-foreground')}>{t(ex.label)}</span>
                            </button>
                        ))}
                        <Button variant='ghost' size='sm' onClick={() => setText('')}>
                            <Eraser className='size-4' />
                            {t({ hi: 'साफ़ करें', en: 'Clear' })}
                        </Button>
                    </div>
                </section>

                {sentence && !hasDevanagari(sentence) && (
                    <p className='rounded-2xl bg-terracotta-soft px-4 py-3 text-sm'>{t({ hi: 'विश्लेषक देवनागरी पढ़ता है। कृपया वाक्य देवनागरी में लिखिए।', en: 'The analyser reads Devanagari. Please type the sentence in Devanagari script.' })}</p>
                )}

                {words.length > 0 && (
                    <>
                        <section className='rounded-[2rem] bg-ink p-5 text-primary-foreground sm:p-7'>
                            <div className='flex flex-wrap items-start justify-between gap-3'>
                                <Skt text={sentence} iast='auto' className='text-3xl font-semibold sm:text-4xl' iastClassName='text-primary-foreground/70' />
                                <AudioButton text={sentence} variant='board' />
                            </div>
                            <p className='mt-4 text-xs font-semibold tracking-wide text-primary-foreground/60 uppercase'>{t({ hi: 'शब्दशः अर्थ (पूरा अनुवाद नहीं)', en: 'Word by word (not a full translation)' })}</p>
                            <div className='mt-2 flex flex-wrap gap-2'>
                                {words.map((w, i) => {
                                    const a = w.analyses.length > 0 ? readingOf(i) : null

                                    return (
                                        <span key={`${w.text}-${i}`} className='rounded-xl bg-primary-foreground/10 px-3 py-1.5'>
                                            <span lang='sa' className='deva block font-semibold text-saffron'>{w.text}</span>
                                            <span className='block text-sm text-primary-foreground/85'>{a ? glossOf(a) : '?'}</span>
                                        </span>
                                    )
                                })}
                            </div>
                        </section>

                        <section className='grid gap-3 sm:grid-cols-3'>
                            <div className='rounded-2xl border border-border bg-card p-4'>
                                <p className='text-xs font-semibold text-muted-foreground'>{t({ hi: 'शब्द', en: 'Words' })}</p>
                                <p className='text-2xl font-semibold'>{words.length}</p>
                            </div>
                            <div className='rounded-2xl border border-border bg-card p-4'>
                                <p className='text-xs font-semibold text-muted-foreground'>{t({ hi: 'पहचाने गए', en: 'Recognised' })}</p>
                                <p className='text-2xl font-semibold'>{known.length} / {words.length}</p>
                            </div>
                            <div className={cn('rounded-2xl border p-4', !hasVerb ? 'border-border bg-card' : issues.length === 0 ? 'border-tulsi bg-tulsi-soft' : 'border-terracotta bg-terracotta-soft')}>
                                <p className='text-xs font-semibold text-muted-foreground'>{t({ hi: 'कर्ता और क्रिया का मेल', en: 'Subject and verb agreement' })}</p>
                                <p className='mt-1 flex items-center gap-1.5 font-semibold'>
                                    {!hasVerb
                                        ? <><CircleHelp className='size-4' />{t({ hi: 'कोई क्रिया नहीं मिली', en: 'No verb found' })}</>
                                        : issues.length === 0
                                            ? <><BadgeCheck className='size-4 text-tulsi' />{t({ hi: 'मेल सही है', en: 'They agree' })}</>
                                            : <><CircleAlert className='size-4 text-terracotta' />{t({ hi: 'मेल में भूल', en: 'They do not agree' })}</>}
                                </p>
                            </div>
                        </section>

                        {issues.length > 0 && (
                            <motion.ul initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className='space-y-2 rounded-2xl bg-terracotta-soft/70 p-4'>
                                {issues.map((issue, i) => (
                                    <li key={i} className='flex items-start gap-2 text-sm'>
                                        <CircleAlert className='mt-0.5 size-4 shrink-0 text-terracotta' />
                                        {t(issue.text)}
                                    </li>
                                ))}
                            </motion.ul>
                        )}

                        {!hasVerb && known.length > 0 && (
                            <p className='rounded-2xl bg-muted/70 px-4 py-3 text-sm'>
                                {t({
                                    hi: 'संस्कृत में "है" (अस्ति) अक्सर छोड़ दिया जाता है, इसलिए बिना क्रिया के वाक्य भी पूरे हो सकते हैं: "एषः रामः" (यह राम है)।',
                                    en: 'Sanskrit often leaves out "is" (अस्ति), so a sentence can be complete without a verb: एषः रामः (this is Rāma).'
                                })}
                            </p>
                        )}

                        <section className='grid gap-4 md:grid-cols-2'>
                            {words.map((w, i) => {
                                const key = `${sentence}:${i}`
                                const a = w.analyses.length > 0 ? readingOf(i) : null
                                const pickIndex = Math.min(chosen[key] ?? 0, Math.max(0, w.analyses.length - 1))

                                return (
                                    <motion.article key={`${w.text}-${i}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.04, 0.3) }} className='rounded-[1.5rem] border border-border bg-card p-5'>
                                        <div className='flex items-start justify-between gap-2'>
                                            <div>
                                                <Skt text={w.text} context={sentence} className='text-3xl font-semibold text-ink' />
                                                {a && <Chip tone='ink' className='mt-1'>{t(KIND_LABEL[a.kind])}</Chip>}
                                            </div>
                                            <AudioButton text={w.text} size='sm' />
                                        </div>
                                        <div className='mt-3'>
                                            {a
                                                ? <ReadingDetails a={a} />
                                                : <p className='text-sm text-muted-foreground'>{t({ hi: 'यह शब्द अभी सुवाणी के शब्दकोश में नहीं है, इसलिए इसका विश्लेषण नहीं हो सका।', en: 'This word is not in the Suvaani dictionary yet, so it could not be analysed.' })}</p>}
                                        </div>
                                        {w.analyses.length > 1 && (
                                            <div className='mt-4 border-t border-border pt-3'>
                                                <p className='text-xs font-semibold text-muted-foreground'>
                                                    {t({ hi: `यह रूप ${w.analyses.length} तरह से पढ़ा जा सकता है। वाक्य के अर्थ से सही चुनिए:`, en: `This form has ${w.analyses.length} possible readings. Let the sentence decide:` })}
                                                </p>
                                                <div className='mt-2 flex flex-wrap gap-1.5'>
                                                    {w.analyses.map((alt, j) => (
                                                        <button
                                                            key={j}
                                                            type='button'
                                                            onClick={() => setChosen(c => ({ ...c, [key]: j }))}
                                                            aria-pressed={pickIndex === j}
                                                            className={cn('rounded-full border px-2.5 py-1 text-xs transition-colors', pickIndex === j ? 'border-ink bg-ink text-primary-foreground' : 'border-border bg-background hover:border-saffron')}
                                                        >
                                                            <span lang='sa' className='deva'>{shortLabel(alt, t)}</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </motion.article>
                                )
                            })}
                        </section>
                    </>
                )}

                <TutorPanel />
            </div>
            </SanskritGate>
        </Layout>
    )
}

export default VakyaPage
